import React, { useMemo, useEffect, useState, useReducer, useCallback } from 'react';
import moment from "moment-timezone";
import {
    Card, CardContent, Typography, TextField, Select, MenuItem, Grid, Snackbar, Alert, IconButton,
    Autocomplete, CircularProgress, Button, Chip, FormControl, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { ExpandLess } from "@mui/icons-material";
import Box from '@mui/material/Box';
import MaterialReactTable from 'material-react-table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import colors from "assets/mavric-theme/base/colors.js";
import typography from "assets/mavric-theme/base/typography";
import borders from "assets/mavric-theme/base/borders";
import { getProbeStreamingActivity, getProbeStreamingActivityForLastMonth, getProbeStreamingAll, getReportTypes, downloadProbeStreamingReport } from 'apis/CustomersApis';


const checkedIcon = <CheckBoxIcon fontSize="small" />;
const { primary, secondary, text, tableRow, transparent, white, black } = colors;
const { size } = typography;
const { borderRadius } = borders;
const ITEM_HEIGHT = 48;


const ProbeStreamingActivity = () => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const columns = useMemo(() => [
        {
            accessorKey: 'customer_id',
            header: 'Customer ID',
            size: 150,
            Filter: ({ header }) => (
                <Autocomplete
                    size="small"
                    options={customerIdOptions}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option}
                    renderOption={(props, option, { selected }) => (
                        <MenuItem {...props} sx={{ "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": { backgroundColor: `${tableRow.hover} !important` } }}> {option}</MenuItem>
                    )}
                    style={{ width: 150 }}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" placeholder="Customer ID" />
                    )}
                    sx={{ '& .MuiAutocomplete-popupIndicator, & .MuiSvgIcon-root': { color: secondary.main } }}
                    onChange={(event, value) =>
                        header.column.setFilterValue(value)}
                    clearIcon={<HighlightOffIcon fontSize="small" />}
                    ListboxProps={{
                        sx: {
                            maxHeight: ITEM_HEIGHT * 4.5,
                        }
                    }}
                />
            ),
            filterFn: 'customerIdFilterFn',

        },
        {
            header: 'Customer Name',
            accessorKey: 'org_name',
            filter: 'equals',
            id: 'org_name',
            size: 150,
            Filter: ({ header }) => (
                <Autocomplete
                    size="small"
                    options={orgNameOptions}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option}
                    renderOption={(props, option, { selected }) => (
                        <MenuItem {...props} sx={{ "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": { backgroundColor: `${tableRow.hover} !important` } }}> {option}</MenuItem>
                    )}
                    style={{ width: 165 }}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" placeholder="Customer Name" />
                    )}
                    sx={{ '& .MuiAutocomplete-popupIndicator, & .MuiSvgIcon-root': { color: secondary.main } }}
                    onChange={(event, value) =>
                        header.column.setFilterValue(value)}
                    clearIcon={<HighlightOffIcon fontSize="small" />}
                    ListboxProps={{
                        sx: {
                            maxHeight: ITEM_HEIGHT * 4.5,
                        }
                    }}
                />
            ),
            filterFn: 'customerNameFilterFn',

        },
        {
            accessorKey: 'total_streaming_duration',
            header: 'Total Streaming Duration',
            size: 200,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value === '00:00:00' ? '-' : value;
            },
            enableColumnFilter: false
        },
        {
            accessorKey: 'first_streamed_at',
            header: 'First Stream Start',
            size: 200,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value
                    ? moment.utc(value).tz(userTimeZone).format("YYYY-MM-DD HH:mm:ss A")
                    : '-';
            },
            enableColumnFilter: false
        },
        {
            accessorKey: 'last_streamed_at',
            header: 'Last Stream End',
            size: 200,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value
                    ? moment.utc(value).tz(userTimeZone).format("YYYY-MM-DD HH:mm:ss A")
                    : '-';
            },
            enableColumnFilter: false
        },
        {
            accessorKey: 'probe_serial_numbers',
            header: 'Probe Serial Numbers',
            size: 300,
            enableColumnFilter: false,
            Cell: ({ cell }) => {
                const [expanded, setExpanded] = useState(false);
                const probes = cell.getValue();
                if (!probes || probes.length === 0) return '-';
                const probesCount = probes.length;
                const visibleProbes = expanded ? probes : [];

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 0.5,
                            flexWrap: 'wrap',
                            alignItems: 'center',
                        }}
                    >

                        {expanded &&
                            visibleProbes.map((serial, index) => (
                                <Chip
                                    key={index}
                                    label={serial}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        fontSize: '13px',
                                        fontWeight: 600,
                                    }}
                                />
                            ))}

                        {!expanded ? (
                            <Box
                                onClick={() => setExpanded(true)}
                                sx={{
                                    color: '#007BCE',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                }}
                            >
                                +{probesCount}
                            </Box>
                        ) : (
                            <Box
                                onClick={() => setExpanded(false)}
                                sx={{
                                    color: '#D32F2F',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    marginLeft: '4px',
                                }}
                            >
                                -
                            </Box>
                        )}
                    </Box>
                );
            },
        }
    ], []
    );

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reportTypes, setReportTypes] = useState([]);
    const [downloading, setDownloading] = useState(false);
    const [currentView, setCurrentView] = useState('thismonth');
    const [orgNameOptions, setOrgNameOptions] = useState([]);
    const [customerIdOptions, setCustomerIdOptions] = useState([]);
    const [probeSerialNumOption, setprobeSerialNumOption] = useState([]);
    const [displayedView, setDisplayedView] = useState('thismonth');
    const [timezone, setTimezone] = useState(userTimeZone);

    const [downloadDialog, dispatch] = useReducer((state, action) => {
        switch (action.type) {
            case "SET_FILENAME":
                return { ...state, filename: action.payload };
            case "SET_OPEN":
                return { ...state, open: action.payload };
            case "SET_BLOB":
                return { ...state, blob: action.payload };
            case "RESET":
                return { filename: "default", open: false, blob: null };
            default:
                return state;
        }
    }, {
        filename: "default",
        open: false,
        blob: null
    });

    const getDisplayLabel = (value) => {
        const labels = {
            'thismonth': 'This Month',
            'lastmonth': 'Last Month',
            'all': 'All'
        };
        return labels[value] || value;
    };

    const fetchReportTypesData = async () => {
        setLoading(true);
        try {
            const response = await getReportTypes();
            // console.log("Data loaded:", response);
            const types = response && response.report_types
            const formattedTypes = types.map(type => ({
                value: type,
                label: type === 'thismonth' ? 'This Month' :
                    type === 'lastmonth' ? 'Last Month' :
                        type === 'all' ? 'All' : type
            }));
            // setReportTypes(formattedTypes);
            setReportTypes(types);

        } catch (error) {
            console.error('Error:', error);
            setReportTypes([]);
        } finally {
            setLoading(false);
        }
    };


    const fetchThisMonthData = async () => {
        setLoading(true);
        try {
            const response = await getProbeStreamingActivity();
            // console.log("Data loaded:", response);
            setData(response);
        } catch (error) {
            console.error('Error:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchLastMonthData = async () => {
        setLoading(true);
        try {
            const response = await getProbeStreamingActivityForLastMonth();
            // console.log("Data loaded:", response);
            setData(response);
        } catch (error) {
            console.error('Error:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const response = await getProbeStreamingAll();
            // console.log("Data loaded:", response);
            setData(response);
        } catch (error) {
            console.error('Error:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchThisMonthData();
        fetchReportTypesData();
    }, []);

    const getViewLabelWithMonth = (view) => {
        const date = new Date();
        if (view === 'thismonth') {
            const monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
            // return `This Month (${monthName})`;
            return monthName;
        } else if (view === 'lastmonth') {
            date.setMonth(date.getMonth() - 1);
            const monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
            // return `Last Month (${monthName})`;
            return monthName;
        } else if (view === 'all') {
            return 'All';
        }

        return 'Select Period';
    };

    const fetchStreamingData = async () => {
        if (!currentView) return;
        setLoading(true);
        try {
            switch (currentView) {
                case 'thismonth':
                    await fetchThisMonthData();
                    break;
                case 'lastmonth':
                    await fetchLastMonthData();
                    break;
                case 'all':
                    await fetchAllData();
                    break;
                default:
                    throw new Error('Invalid selection');
            }
            setDisplayedView(currentView);
            // console.log(`Fetched ${currentView} data:~bj`, response);

        } catch (error) {
            console.error('Error fetching data:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (Array.isArray(data) && data.length > 0) {
            prepareCustomerData(data);
        }
    }, [data]);


    const prepareCustomerData = (data) => {

        let updtdOrgNameOptions = orgNameOptions;
        let updtdCustIdOptions = customerIdOptions;

        data.map(row => {
            const isDuplicateOrgName = updtdOrgNameOptions.includes(row.org_name);
            const isDuplicateCustId = updtdCustIdOptions.includes(row.customer_id);

            if (!isDuplicateOrgName) {
                updtdOrgNameOptions.push(row.org_name);
            }

            if (!isDuplicateCustId) {
                updtdCustIdOptions.push(String(row.customer_id));
            }


        });
        setOrgNameOptions(updtdOrgNameOptions.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
        setCustomerIdOptions(updtdCustIdOptions.sort(((a, b) => parseInt(a) - parseInt(b))));

    };

    const handleSelectionChange = (event) => {
        const selectedValue = event.target.value;
        // console.log("selectedValue:", selectedValue);
        setCurrentView(selectedValue);
    };

    const getProbeStreamingFileName = (period) => {
        const date = new Date();
        const year = date.getFullYear();

        if (period === 'all') {
            return `MX_ProbeStreaming_Activity_Report_${year}.xlsx`;
        } else {
            if (period === 'lastmonth') {
                date.setMonth(date.getMonth() - 1);
            }

            const monthName = date.toLocaleString('default', { month: 'short' });
            const monthYear = date.getFullYear();

            return `MX_ProbeStreaming_Activity_Report_${monthName}_${monthYear}.xlsx`;
        }
    };



    const downloadReport = async () => {
        if (!currentView) return;
        setDownloading(true);

        try {
            const periodMap = {
                'thismonth': 'thismonth',
                'lastmonth': 'lastmonth',
                'all': 'all'
            };

            // const response = await downloadProbeStreamingReport(period);
            // console.log('Download response:~bj', response);
            // Handle the blob response
            // const blob = new Blob([response.data], {
            //   type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            // });
            const period = periodMap[displayedView];
            const payload = {
                query_timezone: timezone,
                duration: period
            }
            const blob = await downloadProbeStreamingReport(payload);
            const filename = getProbeStreamingFileName(period);
            // setDownloadDialog({ open: true, filename: defaultFilename, blob: blob });
            dispatch({ type: "SET_BLOB", payload: blob });
            dispatch({ type: "SET_FILENAME", payload: filename });
            dispatch({ type: "SET_OPEN", payload: true });

        } catch (error) {
            console.error('Download error:', error);
        } finally {
            setDownloading(false);
        }
    };

    const handleFilenameChange = useCallback(
        (e) => dispatch({ type: "SET_FILENAME", payload: e.target.value }), []
    );

    const handleConfirmDownload = () => {
        if (downloadDialog.blob && downloadDialog.filename) {
            const url = window.URL.createObjectURL(downloadDialog.blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = downloadDialog.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
        // dispatch({ type: "SET_OPEN", payload: false })
        dispatch({ type: "RESET" });
    };

    const handleOpenDialog = useCallback(
        () => dispatch({ type: "SET_OPEN", payload: true }), []
    );

    const handleCloseDialog = useCallback(() => dispatch({ type: "SET_OPEN", payload: false }), []);


    return (
        <>
            <Box sx={{
                mt: 3,
                '& .MuiPaper-root': {
                    borderRadius: 3,
                    overflow: 'hidden',
                },
            }}>
                <MaterialReactTable
                    key={`probe-streaming-activity-table-${displayedView}`}
                    columns={columns}
                    data={data || []}
                    state={{ isLoading: loading }}
                    muiTableToolbarAlertBannerProps={{ sx: { display: 'none' } }}
                    muiTablePaperProps={{ sx: { boxShadow: 'none', backgroundColor: primary.main, p: 2 } }}
                    muiTableBodyRowProps={({ row }) => ({
                        hover: true,
                        sx: {
                            color: text.main,
                            align: 'center',
                            '&:nth-of-type(odd)': {
                                backgroundColor: tableRow.dark,
                                border: 0,
                            },
                            '&:nth-of-type(even)': {
                                background: tableRow.light,
                            },
                            // hide last border
                            '&:last-child td, &:last-child th': {
                                border: 0,
                            },
                            '&:hover td': {
                                background: tableRow.hover,
                            },
                            '& td': {
                                lineHeight: 2.1,
                            }
                        },
                    })}
                    muiTableBodyCellProps={{ sx: { border: 'none', color: text.main, align: 'center', boxShadow: 'none', padding: '4px 8px', } }}
                    muiTableProps={{ sx: { backgroundColor: primary.main, color: text.main, boxShadow: 'none' } }}
                    muiTopToolbarProps={{ sx: { backgroundColor: primary.main, boxShadow: 'none', minHeight: 0 } }}
                    muiBottomToolbarProps={{ sx: { backgroundColor: primary.main, boxShadow: 'none' } }}
                    muiTableHeadRowProps={{ sx: { backgroundColor: primary.main, color: text.main, textAlign: 'center' }, }}
                    muiTableHeadCellProps={{ sx: { borderBottom: 0, verticalAlign: 'none', boxShadow: 'none', align: 'center', color: text.main } }}

                    enableColumnActions={false}
                    enableColumnOrdering={false}
                    enablePinning={false}
                    enableSelectAll={false}
                    enableStickyHeader={false}
                    enableClickToCopy={false}

                    positionPagination="bottom"
                    enableToolbarInternalActions={false}
                    initialState={{
                        density: 'compact',
                        showColumnFilters: true,
                        boxShadow: 'none',
                        pagination: { pageSize: 10, pageIndex: 0, pageCount: -1 },
                        sorting: [
                            {
                                id: 'customer_id',
                                desc: false,
                            }
                        ],
                    }}
                    positionActionsColumn="last"
                    muiTablePaginationProps={{
                        rowsPerPageOptions: [3, 5, 10, 15, 20],
                        sx: { color: text.main, '& p': { marginBottom: 0 }, '& .MuiSvgIcon-root': { color: text.main, }, }
                    }}
                    muiTableContainerProps={{
                        sx: {
                            padding: '0 !important',
                        }
                    }}

                    renderTopToolbar={({ table }) => (
                        <Box sx={{ display: 'flex', gap: 2, p: 0.5, pl: 0, pr: 0, justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 2, }}>
                                <FormControl size="medium" sx={{ minWidth: 150 }}>
                                    <Select
                                        value={currentView}
                                        onChange={handleSelectionChange}
                                        disabled={loading}
                                        sx={{
                                            '& .MuiSelect-select': {
                                                paddingTop: '8px !important',
                                                paddingBottom: '8px !important',
                                                minHeight: '30px !important',
                                                display: 'flex',
                                                alignItems: 'center',
                                                py: 1,
                                                fontSize: '0.875rem !important'
                                            },
                                            '& .MuiSvgIcon-root': {
                                                width: 22,
                                                height: 22,
                                                top: 10,
                                            }
                                        }}
                                    >
                                        {reportTypes.map((item, index) => (
                                            <MenuItem key={`timePeriods${index}`} value={item}>
                                                {getDisplayLabel(item)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Button
                                    onClick={fetchStreamingData}
                                    variant="contained"
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: secondary.main,
                                        },
                                    }}
                                    color="secondary"
                                    size="medium"
                                    startIcon={<RefreshIcon />}
                                    disabled={!data || data.length === 0 && loading}
                                >
                                    {loading ? 'fetching...' : 'Fetch Data'}
                                </Button>

                                <Button
                                    onClick={downloadReport}
                                    variant='contained'
                                    color="secondary"
                                    size="medium"
                                    startIcon={<DownloadIcon sx={{
                                        color: (!data || data.length === 0)
                                            ? 'secondary.main'
                                            : 'inherit'
                                    }} />}
                                    disabled={!data || data.length === 0}
                                >
                                    {downloading ? 'Downloading...' : 'Download Report'}
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', pr: 0 }}>
                                <Chip
                                    label={getViewLabelWithMonth(displayedView)}
                                    color="secondary"
                                    variant="outlined"
                                    size="medium"
                                    sx={{
                                        '& .MuiChip-label': {
                                            fontSize: '1rem',
                                            fontWeight: 600
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                    // enableColumnFilters={false}
                    enableSorting={true}

                    filterFns={{
                        customerIdFilterFn: (row, _columnIds, filterValue) => {
                            if (filterValue !== null) {
                                if (filterValue !== 'All') {
                                    return String(row.getValue('customer_id')) === filterValue;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        },
                        customerNameFilterFn: (row, _columnIds, filterValue) => {
                            if (filterValue !== null) {
                                if (filterValue !== 'All') {
                                    return row.getValue('org_name') === filterValue;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        },

                    }}
                />

                <Dialog
                    open={downloadDialog.open}
                    onClose={handleCloseDialog}
                    maxWidth="sm"
                    aria-labelledby="scroll-dialog-title"
                    aria-describedby="scroll-dialog-description"
                    sx={{
                        '& .MuiDialog-paper': {
                            backgroundColor: primary.main,
                        }
                    }}
                    fullWidth
                >

                    <DialogTitle id="alert-dialog-title">
                        <Typography fontSize={size.lg} color={secondary.main} sx={{ fontWeight: 600, }}>{'Confirm Download'}</Typography>
                    </DialogTitle>

                    <DialogContent>
                        <Typography variant="body2" fontSize={size.sm} justifyContent="center" color={text.main} sx={{ fontWeight: 400, }}>Filename</Typography>
                        <TextField
                            type="text"
                            fullWidth
                            focused={false}
                            value={downloadDialog.filename}
                            onChange={handleFilenameChange}
                            // helperText="You can edit the filename before downloading"
                            sx={{
                                // width: '100%',
                                color: text.main,
                                backgroundColor: transparent.main,
                                borderRadius: borderRadius.md,
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} variant="contained" color="secondary">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDownload}
                            color="secondary"
                            variant="contained"
                            disabled={!downloadDialog.filename.trim()}
                        >
                            Download
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
        </>
    );

};

export default ProbeStreamingActivity;